// Vendors
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Actions
import {
  MFA_OPTIONS,
  resendOTP,
  setOTP,
  updateAuthState,
  verifyOTP,
} from '../../../reducers/auth/authActions';

// Selectors
import {
  selectOTP,
  selectIsLoading,
  selectSelectedMFA,
  selectMFAOptions,
  selectFieldErrors,
  selectUtmParams,
} from '../../../reducers/auth/authSelectors';
import { selectSelectedLanguage } from '../../../reducers/language/languageSelectors';

// Helpers
import { accountRecoveryUrl } from '../../../constants/appUrls';
import { buildUrl } from '../../../lib/utmHelpers';

// Styles
import './enter-otp.scss';

// Components
import {
  TxButton,
  TxButtonMode,
  TxLoadingText,
} from 'texkit-ui';
import { Trans } from '../../../components/Trans';

const getHeading = (selectedMFA) => {
  if (selectedMFA === MFA_OPTIONS.EMAIL) {
    return <Trans file="auth" id="enterOtpTitleEmail" />;
  }

  return <Trans file="auth" id="enterOtpTitlePhone" />;
};

const getBodyCopy = (selectedMFA, mfaOptions) => {
  const selectedOption = (mfaOptions || []).find(opt => opt && opt.type === selectedMFA);
  const maskedValue = selectedOption?.value || '';

  if (selectedMFA === MFA_OPTIONS.EMAIL) {
    return (
      <>
        <Trans file="auth" id="enterOtpEmailBodyPrefix" />
        <strong>{maskedValue}</strong>
        <Trans file="auth" id="enterOtpEmailBodyMid" />
      </>
    );
  }

  return (
    <>
      <Trans file="auth" id="enterOtpPhoneBodyPrefix" />
      <strong>{maskedValue}</strong>
      <Trans file="auth" id="enterOtpPhoneBodyMid" />
    </>
  );
};

const EnterOTP = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const otp = useSelector(selectOTP);
  const isLoading = useSelector(selectIsLoading);
  const serverFieldErrors = useSelector(selectFieldErrors);
  const selectedMFA = useSelector(selectSelectedMFA);
  const mfaOptions = useSelector(selectMFAOptions);
  const utmParams = useSelector(selectUtmParams);
  const langParam = useSelector(selectSelectedLanguage);
  const { t } = useTranslation('auth');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [otpFieldError, setOtpFieldError] = useState(null);

  useEffect(() => {
    if (resendSecondsLeft <= 0) {
      return undefined;
    }

    const timerId = setInterval(() => {
      setResendSecondsLeft(prev => (prev > 1 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timerId);
  }, [resendSecondsLeft]);

  // Sync server field errors to local state when they arrive
  useEffect(() => {
    if (serverFieldErrors.otp) {
      setOtpFieldError(serverFieldErrors.otp);
    }
  }, [serverFieldErrors]);

  const isEmailSelected = selectedMFA === MFA_OPTIONS.EMAIL;
  const stepClassName = `step enter-otp-step ${isEmailSelected ? 'enter-otp-step-email' : 'enter-otp-step-sms'}`;
  const titleClassName = `step-title enter-otp-title ${isEmailSelected ? 'enter-otp-title-email' : 'enter-otp-title-sms'}`;
  const subtitleClassName = `step-subtitle enter-otp-subtitle ${isEmailSelected ? 'enter-otp-subtitle-email' : 'enter-otp-subtitle-sms'}`;
  const footerClassName = `enter-otp-footer ${isEmailSelected ? 'enter-otp-footer-email' : 'enter-otp-footer-sms'}`;
  const isResendCoolingDown = resendSecondsLeft > 0;
  const hasShortCodeError = otp.length > 0 && otp.length < 6;
  const hasServerError = Boolean(otpFieldError);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = await dispatch(verifyOTP(otp, rememberDevice));
    if (success) {
      navigate('/verify-consent', { replace: true });
    }
  };

  const handleResend = async () => {
    if (isResendCoolingDown || isLoading || isResending) {
      return;
    }

    dispatch(setOTP(''));
    dispatch(updateAuthState({ fieldErrors: {} }));
    setOtpFieldError(null);
    setIsResending(true);
    try {
      const isResent = await dispatch(resendOTP());
      if (isResent) {
        setResendSecondsLeft(30);
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleOpenAccountRecovery = () => {
    window.open(buildUrl(`${accountRecoveryUrl}?${langParam}`, utmParams), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={stepClassName}>
      <h1 className={titleClassName}>{getHeading(selectedMFA)}</h1>
      <p className={subtitleClassName}>
        {getBodyCopy(selectedMFA, mfaOptions)}
      </p>

      <form
        className="step-form enter-otp-form"
        onSubmit={handleSubmit}
        noValidate
      >
        <div
          className={`enter-otp-input-field ${hasShortCodeError || hasServerError ? "enter-otp-input-field-short-error" : ""}`}
        >
          <div className="enter-otp-input-frame">
            <input
              className="otp-input"
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={(e) => {
                dispatch(setOTP(e.target.value.replace(/\D/g, "")));
                // Clear server error when user types
                if (otpFieldError) {
                  setOtpFieldError(null);
                  dispatch(updateAuthState({ fieldErrors: {} }));
                }
              }}
              placeholder={t("otpPlaceholder")}
              autoComplete="one-time-code"
              aria-invalid={hasShortCodeError || hasServerError}
            />
          </div>

          {hasShortCodeError && !hasServerError && (
            <div className="enter-otp-inline-error" role="alert">
              <p className="enter-otp-inline-error-message">
                <Trans file="auth" id="otpShortCodeError" />
              </p>
            </div>
          )}

          {hasServerError && (
            <div className="enter-otp-inline-error" role="alert">
              <p className="enter-otp-inline-error-message">
                <strong className="enter-otp-inline-error-heading">
                  <Trans file="auth" id="otpErrorTitle" />
                </strong>{' '}
                <Trans file="auth" id={otpFieldError} />
              </p>
            </div>
          )}
        </div>

        <div
          className="enter-otp-links"
          role="group"
          aria-label="One-time passcode actions"
        >
          {isResendCoolingDown ? (
            <p className="enter-otp-resend-timer">
              {t("resendCodeTimer", { seconds: resendSecondsLeft })}
            </p>
          ) : (
            <TxButton
              type="button"
              className="enter-otp-resend-link"
              onClick={handleResend}
              disabled={isResending || isLoading}
              aria-busy={isResending}
            >
              {isResending ? (
                <TxLoadingText>
                  <Trans file="auth" id="submitting" />
                </TxLoadingText>
              ) : (
                <Trans file="auth" id="resendCode" />
              )}
            </TxButton>
          )}
        </div>

        <div className="enter-otp-remember-option">
          <input
            id="remember-device-checkbox"
            className="enter-otp-remember-checkbox"
            type="checkbox"
            checked={rememberDevice}
            onChange={(e) => setRememberDevice(e.target.checked)}
            aria-label="Remember this device"
          />
          <label
            className="enter-otp-remember-text"
            htmlFor="remember-device-checkbox"
          >
            <Trans file="auth" id="rememberDevice" />
          </label>
        </div>

        <div className={footerClassName}>
          <div className="enter-otp-actions">
            <TxButton
              className="enter-otp-submit"
              mode={TxButtonMode.Primary}
              type="submit"
              disabled={isLoading || otp.length < 6}
            >
              {isLoading ? (
                <TxLoadingText>
                  <Trans file="auth" id="submit" />
                </TxLoadingText>
              ) : (
                <Trans file="auth" id="submit" />
              )}
            </TxButton>
          </div>

          {isEmailSelected && (
            <p className="enter-otp-recovery-help">
              <strong><Trans file="auth" id="emailAccountRecoveryPrefix" /></strong> {' '}
              <Trans file="auth" id="accountRecoveryMid" /> {' '}
              <TxButton
                type="button"
                className="enter-otp-recovery-link"
                onClick={handleOpenAccountRecovery}
              >
                <Trans file="auth" id="deactivateAccount" />
              </TxButton>
              <Trans file="auth" id="accountRecoverySuffix" />
            </p>
          )}
        </div>
      </form>
    </div>
  );
};

export default EnterOTP;
